/**
 * Utility functions for the application
 */

import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { toast } from "@/components/ui/use-toast";
import { normalizeBehaviorType } from "./behavior-utils";
import type { Behavior } from "@/context/functionalBehaviorAnalysisContext";

/**
 * Merge class names with tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Safe string utility - returns empty string if input is null or undefined
 */
export const safeString = (str: string | undefined | null): string => str || "";

/**
 * Safe array utility - returns empty array if input is null or undefined
 */
export const safeArray = <T,>(arr: T[] | undefined | null): T[] => arr || [];

/**
 * Safe object utility - returns empty object if input is null or undefined
 */
export const safeObject = <T extends object>(obj: T | undefined | null): T => 
  obj || {} as T;

/**
 * Safe word count utility - counts words in a string
 */
export const safeWordCount = (text: string | undefined | null): number => {
  if (!text || typeof text !== 'string') return 0;
  return text.split(/\s+/).filter(Boolean).length;
};

/**
 * Handle API errors with appropriate toast notifications
 */
export function handleApiError(error: unknown, fallbackMessage = "An error occurred"): void {
  console.error("API Error:", error);
  
  let errorMessage = fallbackMessage;
  
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === "string") {
    errorMessage = error;
  } else if (typeof error === "object" && error && "message" in error) {
    errorMessage = String((error as any).message);
  }
  
  toast({
    title: "Error",
    description: errorMessage,
    variant: "destructive",
  });
}

/**
 * Enhanced error handling for behavior extraction
 * Provides specific error messages for common issues
 */
export function handleBehaviorExtractionError(error: unknown): void {
  console.error("Behavior Extraction Error:", error);
  
  let errorMessage = "Failed to extract behaviors";
  let errorDetail = "";
  
  if (error instanceof Error) {
    if (error.message.includes("No case description")) {
      errorMessage = "Missing Case Information";
      errorDetail = "Please provide case notes or description before extraction.";
    } else if (error.message.includes("401")) {
      errorMessage = "Authentication Error";
      errorDetail = "API key missing or invalid. Please check your configuration.";
    } else if (error.message.includes("429")) {
      errorMessage = "Rate Limit Exceeded";
      errorDetail = "Too many requests. Please try again later.";
    } else if (error.message.includes("500")) {
      errorMessage = "Server Error";
      errorDetail = "The AI service encountered an error. Please try again later.";
    } else {
      errorDetail = error.message;
    }
  }
  
  toast({
    title: errorMessage,
    description: errorDetail || "Please try again or check your connection.",
    variant: "destructive",
    duration: 5000,
  });
}

/**
 * Validate behavior data for operant-first workflow
 * Returns true if valid, false if invalid
 */
export function validateBehaviorData(behavior: Partial<Behavior>): boolean {
  // Basic validation
  if (!behavior.description?.trim()) {
    toast({
      title: "Missing Information",
      description: "Behavior description is required.",
      variant: "destructive",
    });
    return false;
  }
  
  // Normalize behavior type
  const normalizedType = normalizeBehaviorType(behavior.type);
  
  // For operant behaviors, ensure we have basic details
  if (normalizedType === "operant") {
    if (behavior.frequency?.value == null && behavior.duration?.value == null && behavior.intensity == null) {
      toast({
        title: "Missing Details",
        description: "Please provide at least one detail (frequency, duration, or intensity).",
        variant: "destructive",
      });
      return false;
    }
  }
  
  return true;
}

/**
 * Format error message for user display
 */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (typeof error === "object" && error && "message" in error) {
    return String((error as any).message);
  }
  return "An unexpected error occurred";
}